import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, AlertTriangle, Search, ShieldCheck, ChevronLeft, ChevronRight, RefreshCw, FileText, Filter } from 'lucide-react';
import { toast } from 'sonner';

function fmtMonth(d) { return d.toISOString().slice(0, 7); }
const fmtCur = (n, c = 'AED') => `${c} ${(n || 0).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function FieldOverride({ label, hint, value, setValue, placeholder, testid, small }) {
  const isOverridden = value !== '' && value != null;
  return (
    <div>
      <label className={`${small ? 'text-[11px]' : 'text-xs'} font-medium flex items-center justify-between gap-1`}>
        <span>{label}</span>
        {isOverridden && (
          <button
            type="button"
            onClick={() => setValue('')}
            className="text-[10px] text-muted-foreground hover:text-red-500"
            data-testid={`${testid}-clear`}
          >
            ✕ clear
          </button>
        )}
      </label>
      <Input
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 ${isOverridden ? 'border-violet-500 bg-violet-500/5 text-violet-700 font-medium' : ''} ${small ? 'h-8 text-xs' : ''}`}
        data-testid={testid}
      />
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function FieldText({ label, value, setValue, placeholder, testid }) {
  const isOverridden = value !== '' && value != null;
  return (
    <div>
      <label className="text-xs font-medium flex items-center justify-between gap-1">
        <span>{label}</span>
        {isOverridden && (
          <button
            type="button"
            onClick={() => setValue('')}
            className="text-[10px] text-muted-foreground hover:text-red-500"
          >
            ✕ clear
          </button>
        )}
      </label>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 ${isOverridden ? 'border-violet-500 bg-violet-500/5 text-violet-700 font-medium' : ''}`}
        data-testid={testid}
      />
    </div>
  );
}

export default function PrePayrollVerificationPage() {
  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [data, setData] = useState({ summary: {}, rows: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editing, setEditing] = useState(null);
  const [hrNote, setHrNote] = useState('');
  const [extraDeduction, setExtraDeduction] = useState('0');
  const [bracketOverride, setBracketOverride] = useState('');
  const [currentSalaryOv, setCurrentSalaryOv] = useState('');
  const [commissionOv, setCommissionOv] = useState('');
  const [halfDayOv, setHalfDayOv] = useState('');
  const [lopDaysOv, setLopDaysOv] = useState('');
  const [lateOv, setLateOv] = useState('');
  const [sickOv, setSickOv] = useState('');
  const [annualOv, setAnnualOv] = useState('');
  const [manualBonusOv, setManualBonusOv] = useState('');
  const [targetBonusOv, setTargetBonusOv] = useState('');
  const [categoryOv, setCategoryOv] = useState('');
  const [ceoBonus, setCeoBonus] = useState('0');
  const [ceoBonusNote, setCeoBonusNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/hr/payroll/pre-run/${month}`);
      setData(res.data || { summary: {}, rows: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load pre-payroll table');
      setData({ summary: {}, rows: [] });
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const departments = useMemo(() => {
    const set = new Set((data.rows || []).map(r => r.department).filter(Boolean));
    return Array.from(set).sort();
  }, [data.rows]);

  const filteredRows = useMemo(() => {
    return (data.rows || []).filter(r => {
      if (filterDept !== 'all' && r.department !== filterDept) return false;
      if (filterStatus === 'verified' && !r.verified) return false;
      if (filterStatus === 'pending' && r.verified) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.employee_name?.toLowerCase().includes(s)
              || r.employee_code?.toLowerCase().includes(s)
              || r.department?.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [data.rows, filterDept, filterStatus, search]);

  const verify = async (row, verified = true) => {
    try {
      await api.post(`/hr/payroll/pre-run/${month}/verify`, {
        employee_id: row.employee_id,
        verified,
        hr_note: row.hr_note || '',
        hr_extra_deduction: row.hr_extra_deduction || 0,
      });
      toast.success(verified ? 'Verified' : 'Unverified');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verify failed');
    }
  };

  const verifyAll = async () => {
    if (!window.confirm(`Bulk-verify ALL ${data.summary?.total_employees} employees for ${month}?`)) return;
    try {
      await api.post(`/hr/payroll/pre-run/${month}/verify-all`);
      toast.success('All employees verified');
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk verify failed');
    }
  };

  const verifySelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.info('No rows selected');
      return;
    }
    if (!window.confirm(`Verify ${ids.length} selected employee(s)?`)) return;
    try {
      await api.post(`/hr/payroll/pre-run/${month}/verify-all`, { employee_ids: ids });
      toast.success(`${ids.length} verified`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk verify failed');
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/hr/payroll/pre-run/${month}/export.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pre-payroll-${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch {
      toast.error('Excel export failed');
    }
  };

  const toggleRow = (empId) => {
    const next = new Set(selectedIds);
    if (next.has(empId)) next.delete(empId);
    else next.add(empId);
    setSelectedIds(next);
  };

  const openEdit = (row) => {
    setEditing(row);
    setHrNote(row.hr_note || '');
    setExtraDeduction(String(row.hr_extra_deduction || 0));
    setBracketOverride(row.bracket_override != null ? String(row.bracket_override) : '');
    setCurrentSalaryOv(row.current_salary_override != null ? String(row.current_salary_override) : '');
    setCommissionOv(row.commission_override != null ? String(row.commission_override) : '');
    setHalfDayOv(row.half_day_override != null ? String(row.half_day_override) : '');
    setLopDaysOv(row.lop_days_override != null ? String(row.lop_days_override) : '');
    setLateOv(row.late_override != null ? String(row.late_override) : '');
    setSickOv(row.sick_override != null ? String(row.sick_override) : '');
    setAnnualOv(row.annual_override != null ? String(row.annual_override) : '');
    setManualBonusOv(row.manual_bonus_override != null ? String(row.manual_bonus_override) : '');
    setTargetBonusOv(row.target_bonus_override != null ? String(row.target_bonus_override) : '');
    setCategoryOv(row.category_override || '');
    setCeoBonus(String(row.ceo_bonus || 0));
    setCeoBonusNote(row.ceo_bonus_note || '');
  };

  const saveEdit = async () => {
    try {
      const numOrNull = (s) => (s === '' ? null : parseFloat(s));
      await api.post(`/hr/payroll/pre-run/${month}/verify`, {
        employee_id: editing.employee_id,
        verified: true,
        hr_note: hrNote,
        hr_extra_deduction: parseFloat(extraDeduction) || 0,
        bracket_override: numOrNull(bracketOverride),
        current_salary_override: numOrNull(currentSalaryOv),
        commission_override: numOrNull(commissionOv),
        half_day_override: numOrNull(halfDayOv),
        lop_days_override: numOrNull(lopDaysOv),
        late_override: numOrNull(lateOv),
        sick_override: numOrNull(sickOv),
        annual_override: numOrNull(annualOv),
        manual_bonus_override: numOrNull(manualBonusOv),
        target_bonus_override: numOrNull(targetBonusOv),
        category_override: categoryOv || null,
        ceo_bonus: parseFloat(ceoBonus) || 0,
        ceo_bonus_note: ceoBonusNote,
      });
      toast.success('Saved & verified');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  // Live What-If preview based on current override inputs
  const whatIf = useMemo(() => {
    if (!editing) return null;
    const c = editing.currency || 'AED';
    const num = (s, def) => (s === '' || s == null || isNaN(parseFloat(s)) ? def : parseFloat(s));
    const contracted = num(currentSalaryOv, editing.contracted_gross ?? ((editing.basic_salary || 0) + (editing.allowances_total || 0)));
    const overrideNum = bracketOverride === '' ? null : parseFloat(bracketOverride);
    // Effective gross: bracket_override wins; else if user changed current_salary, use that; else original gross
    const effGross = (overrideNum != null && !isNaN(overrideNum))
      ? overrideNum
      : (currentSalaryOv !== '' ? contracted : (editing.gross_salary || 0));
    const bonusDelta = effGross - contracted;
    const wd = editing.working_days || 1;
    const dailyRate = wd > 0 ? effGross / wd : 0;
    const lopDays = num(lopDaysOv, editing.lop_days || 0);
    const halfDays = num(halfDayOv, editing.half_day || 0);
    const lopDed = lopDays * dailyRate;
    const halfDed = halfDays * 0.5 * dailyRate;
    const extra = parseFloat(extraDeduction) || 0;
    const commission = num(commissionOv, editing.commission_amount || 0);
    const targetB = num(targetBonusOv, editing.target_bonus || 0);
    const manualB = num(manualBonusOv, editing.manual_bonus || 0);
    const ceoB = parseFloat(ceoBonus) || 0;
    const finalPayout = effGross + commission + targetB + manualB + ceoB - lopDed - halfDed - extra;
    return {
      currency: c, contracted, effGross, bonusDelta, lopDed, halfDed, extra,
      commission, targetB, manualB, ceoB, lopDays, halfDays,
      finalPayout: Math.max(0, finalPayout),
      hasOverride: overrideNum != null && !isNaN(overrideNum) && overrideNum !== editing.gross_salary,
    };
  }, [editing, bracketOverride, extraDeduction, currentSalaryOv, commissionOv, halfDayOv, lopDaysOv, manualBonusOv, targetBonusOv, ceoBonus]);

  const monthDate = new Date(month + '-01');
  const prevMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() - 1); setMonth(fmtMonth(d)); };
  const nextMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() + 1); setMonth(fmtMonth(d)); };
  const monthLabel = monthDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const s = data.summary || {};
  const ready = !!s.ready_to_run;

  return (
    <div className="space-y-6" data-testid="pre-payroll-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-500" /> Pre-Payroll Verification
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Review every employee&apos;s salary, bonus, attendance &amp; leaves. HR must verify each row before payroll engine can run.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="pp-month-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-32 text-center">{monthLabel}</span>
          <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="pp-month-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={load}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      <Card className={ready ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}>
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {ready ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            )}
            <div>
              <p className={`font-semibold ${ready ? 'text-emerald-700' : 'text-amber-700'}`}>
                {ready ? 'Ready for Payroll Run' : 'Verification Pending'}
              </p>
              <p className="text-xs text-muted-foreground">
                {s.verified || 0} verified · {s.pending || 0} pending · {s.total_employees || 0} total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="pp-export-xlsx-btn">
              <FileText className="h-4 w-4 mr-1" /> Export Excel
            </Button>
            <Button variant="outline" size="sm" onClick={verifySelected} disabled={selectedIds.size === 0} data-testid="pp-verify-selected-btn">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Verify Selected ({selectedIds.size})
            </Button>
            <Button variant="outline" size="sm" onClick={verifyAll} data-testid="pp-verify-all-btn">
              <ShieldCheck className="h-4 w-4 mr-1" /> Verify All
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payout Comparison Banner — Per Currency */}
      {(s.by_currency && s.by_currency.length > 0) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="pp-currency-banners">
          {s.by_currency.map((b) => {
            const variance = (b.total_payout || 0) - (b.total_defined_payout || 0);
            const sign = variance > 0 ? '+' : '';
            const vCls = variance > 0 ? 'text-emerald-700' : variance < 0 ? 'text-red-600' : 'text-muted-foreground';
            const accent = b.currency === 'INR' ? 'border-l-amber-500 from-amber-500/5' : 'border-l-emerald-500 from-emerald-500/5';
            return (
              <Card key={b.currency} className={`border-l-4 ${accent} bg-gradient-to-r to-transparent`} data-testid={`pp-currency-${b.currency}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold tracking-wide">{b.currency} Payroll</span>
                      <Badge variant="outline" className="text-xs">{b.employee_count} employees</Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {b.verified} verified · {b.pending} pending
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">As-Defined</p>
                      <p className="text-xl font-bold font-mono" data-testid={`pp-defined-${b.currency}`}>
                        {fmtCur(b.total_defined_payout || 0, b.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Actual</p>
                      <p className="text-xl font-bold font-mono text-emerald-700" data-testid={`pp-actual-${b.currency}`}>
                        {fmtCur(b.total_payout || 0, b.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Variance</p>
                      <p className={`text-xl font-bold font-mono ${vCls}`} data-testid={`pp-variance-${b.currency}`}>
                        {sign}{fmtCur(variance, b.currency)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
                    <span>Commission {fmtCur(b.total_commission || 0, b.currency)}</span>
                    <span>·</span>
                    <span>Target bonus {fmtCur(b.total_target_bonus || 0, b.currency)}</span>
                    <span>·</span>
                    <span>LOP {b.total_lop_days || 0}d</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-500/5 to-transparent">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">As-Defined Payout</p>
                <p className="text-2xl font-bold font-mono" data-testid="pp-defined-payout">
                  {fmtCur(s.total_defined_payout || 0, 'AED')}
                </p>
                <p className="text-[11px] text-muted-foreground">Sum of contracted gross (basic + allowances) of {s.total_employees || 0} employees</p>
              </div>
              <div className="text-2xl text-muted-foreground">→</div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Actual Payout</p>
                <p className="text-2xl font-bold font-mono text-emerald-700" data-testid="pp-actual-payout">
                  {fmtCur(s.total_payout || 0, 'AED')}
                </p>
                <p className="text-[11px] text-muted-foreground">Bracket + commission + bonuses − deductions</p>
              </div>
              <div className="text-2xl text-muted-foreground">=</div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Variance</p>
                {(() => {
                  const variance = (s.total_payout || 0) - (s.total_defined_payout || 0);
                  const sign = variance > 0 ? '+' : '';
                  const cls = variance > 0 ? 'text-emerald-700' : variance < 0 ? 'text-red-600' : 'text-muted-foreground';
                  return <p className={`text-2xl font-bold font-mono ${cls}`} data-testid="pp-variance">{sign}{fmtCur(variance, 'AED')}</p>;
                })()}
                <p className="text-[11px] text-muted-foreground">
                  {s.probation_employees > 0 && `${s.probation_employees} on probation · `}
                  Total target bonus {fmtCur(s.total_target_bonus || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aggregate KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Working Days</p>
          <p className="text-2xl font-bold">{s.working_days || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total Late</p>
          <p className="text-2xl font-bold text-amber-600">{s.total_late_count || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Half Days</p>
          <p className="text-2xl font-bold text-orange-600">{s.total_half_days || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">LOP Days</p>
          <p className="text-2xl font-bold text-red-600">{s.total_lop_days || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total Commission</p>
          <div className="space-y-0.5" data-testid="pp-total-commission">
            {(s.by_currency && s.by_currency.length > 0) ? s.by_currency.map((b) => (
              <p key={b.currency} className="text-lg font-bold text-emerald-600 leading-tight font-mono">
                {fmtCur(b.total_commission || 0, b.currency)}
              </p>
            )) : (
              <p className="text-2xl font-bold text-emerald-600">{fmtCur(s.total_commission || 0, 'AED')}</p>
            )}
          </div>
        </CardContent></Card>
        <Card className="border-emerald-500/40 bg-emerald-500/5"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total Payout</p>
          <div className="space-y-0.5" data-testid="pp-total-payout">
            {(s.by_currency && s.by_currency.length > 0) ? s.by_currency.map((b) => (
              <p key={b.currency} className="text-lg font-bold text-emerald-700 leading-tight font-mono">
                {fmtCur(b.total_payout || 0, b.currency)}
              </p>
            )) : (
              <p className="text-2xl font-bold text-emerald-700">{fmtCur(s.total_payout || 0, 'AED')}</p>
            )}
          </div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Filter className="h-4 w-4" /> Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name / code / department"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[280px]"
              data-testid="pp-search"
            />
          </div>
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-[200px]" data-testid="pp-filter-dept"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="pp-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline">{filteredRows.length} shown</Badge>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[11px]">
                <tr>
                  <th className="text-center px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={filteredRows.length > 0 && filteredRows.every(r => selectedIds.has(r.employee_id))}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) {
                          filteredRows.forEach(r => next.add(r.employee_id));
                        } else {
                          filteredRows.forEach(r => next.delete(r.employee_id));
                        }
                        setSelectedIds(next);
                      }}
                      data-testid="pp-select-all-checkbox"
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-medium sticky left-0 bg-muted/60">Employee</th>
                  <th className="text-left px-2 py-2 font-medium">Dept</th>
                  <th className="text-right px-2 py-2 font-medium">Current<br />Salary</th>
                  <th className="text-left px-2 py-2 font-medium">Category</th>
                  <th className="text-right px-2 py-2 font-medium">Bracket<br />Salary</th>
                  <th className="text-right px-2 py-2 font-medium">Bonus<br />(±)</th>
                  <th className="text-right px-2 py-2 font-medium">Commission</th>
                  <th className="text-right px-2 py-2 font-medium">Target<br />Bonus %</th>
                  <th className="text-right px-2 py-2 font-medium">Manual<br />Bonus</th>
                  <th className="text-right px-2 py-2 font-medium">Late /<br />Half</th>
                  <th className="text-right px-2 py-2 font-medium">Leaves<br />(S/A/O)</th>
                  <th className="text-right px-2 py-2 font-medium">LOP</th>
                  <th className="text-right px-2 py-2 font-medium">Extra<br />Deduct</th>
                  <th className="text-right px-2 py-2 font-medium bg-emerald-500/10">Total<br />Payout</th>
                  <th className="text-center px-2 py-2 font-medium sticky right-0 bg-muted/60">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={16} className="p-10 text-center">
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                  </td></tr>
                ) : filteredRows.length === 0 ? (
                  <tr><td colSpan={16} className="p-10 text-center text-muted-foreground">No rows match filters</td></tr>
                ) : filteredRows.map(r => (
                  <tr
                    key={r.employee_id}
                    className={`border-b hover:bg-muted/20 ${r.verified ? '' : 'bg-amber-500/5'}`}
                    data-testid={`pp-row-${r.employee_id}`}
                  >
                    <td className="text-center px-2 py-2">
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        checked={selectedIds.has(r.employee_id)}
                        onChange={() => toggleRow(r.employee_id)}
                        data-testid={`pp-select-${r.employee_id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium sticky left-0 bg-background">
                      {r.employee_name}
                      <p className="text-[10px] text-muted-foreground">
                        {r.employee_code} · {r.role}
                      </p>
                    </td>
                    <td className="px-2 py-2">{r.department || '—'}</td>
                    <td className="px-2 py-2 text-right">
                      <p className="font-mono">{fmtCur((r.contracted_gross ?? (r.basic_salary + (r.allowances_total||0))), r.currency)}</p>
                      <p className="text-[10px] text-muted-foreground">basic + allow</p>
                    </td>
                    <td className="px-2 py-2">
                      {r.category_exempt ? (
                        <>
                          <Badge variant="outline" className="text-[10px] bg-violet-500/10 text-violet-700 border-violet-500/40">Probation</Badge>
                          <p className="text-[10px] text-muted-foreground mt-0.5">no gating</p>
                        </>
                      ) : r.category_name ? (
                        <>
                          <Badge variant="secondary" className="text-[10px]">{r.category_name}</Badge>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{r.category_accounts} acc · {fmtCur(r.category_revenue)}</p>
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.category_exempt ? (
                        <span className="text-muted-foreground text-[10px]">exempt</span>
                      ) : r.category_name ? (
                        <span className="font-mono">{fmtCur(r.category_salary || 0, r.currency)}</span>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.category_name ? (
                        r.salary_bonus_above_bracket > 0
                          ? <span className="text-emerald-600 font-mono">+{fmtCur(r.salary_bonus_above_bracket, r.currency)}</span>
                          : r.salary_bonus_above_bracket < 0
                            ? <span className="text-red-600 font-mono">{fmtCur(r.salary_bonus_above_bracket, r.currency)}</span>
                            : <span className="text-muted-foreground font-mono">0</span>
                      ) : '—'}
                      {r.bracket_override != null && (
                        <p className="text-[10px] text-violet-600 mt-0.5">override</p>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.commission_amount > 0
                        ? <span className="text-emerald-600 font-mono">{fmtCur(r.commission_amount, r.currency)}</span>
                        : '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.target_bonus > 0 ? (
                        <>
                          <span className="text-emerald-600 font-mono">{fmtCur(r.target_bonus, r.currency)}</span>
                          <p className="text-[10px] text-muted-foreground">{r.target_bonus_pct}% on {fmtCur(r.target_revenue || 0)}</p>
                        </>
                      ) : (r.target_revenue > 0 ? (
                        <span className="text-muted-foreground text-[10px]">below 40k</span>
                      ) : '—')}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.manual_bonus > 0
                        ? <span className="text-emerald-600 font-mono">{fmtCur(r.manual_bonus, r.currency)}</span>
                        : '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <span className="text-amber-600">{r.late_count}</span>
                      {' / '}
                      <span className="text-orange-600">{r.half_day}</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <span className="text-blue-600">{r.leaves_sick}</span>
                      {' / '}
                      <span className="text-violet-600">{r.leaves_annual}</span>
                      {' / '}
                      <span className="text-muted-foreground">{r.leaves_other}</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.lop_days > 0 ? (
                        <>
                          <span className="text-red-600 font-mono">{r.lop_days}d</span>
                          <p className="text-[10px] text-muted-foreground">−{fmtCur(r.lop_deduction, r.currency)}</p>
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.hr_extra_deduction > 0
                        ? <span className="text-red-600 font-mono">−{fmtCur(r.hr_extra_deduction, r.currency)}</span>
                        : '—'}
                    </td>
                    <td className="px-2 py-2 text-right bg-emerald-500/5">
                      <span className={`font-mono font-semibold ${r.final_payout > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {fmtCur(r.final_payout || 0, r.currency)}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center sticky right-0 bg-background">
                      {r.verified ? (
                        <div className="space-y-1">
                          <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30 hover:bg-emerald-500/20">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Verified
                          </Badge>
                          <div className="flex gap-1 justify-center">
                            <Button
                              size="sm" variant="ghost" className="h-6 text-[10px]"
                              onClick={() => openEdit(r)}
                              data-testid={`pp-edit-${r.employee_id}`}
                            >Edit</Button>
                            <Button
                              size="sm" variant="ghost" className="h-6 text-[10px] text-red-600"
                              onClick={() => verify(r, false)}
                              data-testid={`pp-unverify-${r.employee_id}`}
                            >Undo</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <Button
                            size="sm" className="h-7 text-[11px]"
                            onClick={() => openEdit(r)}
                            data-testid={`pp-verify-${r.employee_id}`}
                          >
                            Verify
                          </Button>
                        </div>
                      )}
                      {r.hr_note && (
                        <p className="text-[10px] text-muted-foreground mt-1 italic max-w-[120px] truncate">{r.hr_note}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Verify Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                <FileText className="h-5 w-5" /> Verify {editing?.employee_name}
                <Badge variant="outline" className="text-[10px] ml-2">{editing?.employee_code} · {editing?.department}</Badge>
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
            <p className="text-[11px] text-muted-foreground">
              Every value is pulled from the system. Edit any field to override for this month only. Original values are shown as placeholders. Empty = use system value.
            </p>

            {/* Salary block */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <FieldOverride
                label={`Current Salary (${editing?.currency})`}
                hint="basic + allowances"
                value={currentSalaryOv}
                setValue={setCurrentSalaryOv}
                placeholder={String(editing?.contracted_gross ?? ((editing?.basic_salary || 0) + (editing?.allowances_total || 0)))}
                testid="pp-edit-current-salary"
              />
              <FieldOverride
                label={`Bracket Salary (${editing?.currency})`}
                hint="auto from category — overrides gross"
                value={bracketOverride}
                setValue={setBracketOverride}
                placeholder={String(editing?.gross_salary || 0)}
                testid="pp-edit-bracket-override"
              />
              <FieldText
                label="Category"
                value={categoryOv}
                setValue={setCategoryOv}
                placeholder={editing?.category_name || 'Auto'}
                testid="pp-edit-category"
              />
            </div>

            {/* Bonus block */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <FieldOverride
                label={`Total Commission (${editing?.currency})`}
                hint="from sales engine"
                value={commissionOv}
                setValue={setCommissionOv}
                placeholder={String(editing?.commission_amount || 0)}
                testid="pp-edit-commission"
              />
              <FieldOverride
                label={`Target Bonus (${editing?.currency})`}
                hint={editing?.target_bonus_pct ? `auto: ${editing.target_bonus_pct}%` : 'auto'}
                value={targetBonusOv}
                setValue={setTargetBonusOv}
                placeholder={String(editing?.target_bonus || 0)}
                testid="pp-edit-target-bonus"
              />
              <FieldOverride
                label={`Manual Bonus (${editing?.currency})`}
                hint="auto from bonuses table"
                value={manualBonusOv}
                setValue={setManualBonusOv}
                placeholder={String(editing?.manual_bonus || 0)}
                testid="pp-edit-manual-bonus"
              />
            </div>

            {/* Attendance block */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <FieldOverride
                label="Late"
                value={lateOv}
                setValue={setLateOv}
                placeholder={String(editing?.late_count || 0)}
                testid="pp-edit-late"
                small
              />
              <FieldOverride
                label="Half Days"
                value={halfDayOv}
                setValue={setHalfDayOv}
                placeholder={String(editing?.half_day || 0)}
                testid="pp-edit-half"
                small
              />
              <FieldOverride
                label="Sick Leave"
                value={sickOv}
                setValue={setSickOv}
                placeholder={String(editing?.leaves_sick || 0)}
                testid="pp-edit-sick"
                small
              />
              <FieldOverride
                label="Annual Leave"
                value={annualOv}
                setValue={setAnnualOv}
                placeholder={String(editing?.leaves_annual || 0)}
                testid="pp-edit-annual"
                small
              />
              <FieldOverride
                label="LOP Days"
                value={lopDaysOv}
                setValue={setLopDaysOv}
                placeholder={String(editing?.lop_days || 0)}
                testid="pp-edit-lop"
                small
              />
            </div>

            {/* HR / CEO block */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border rounded-md p-3 bg-amber-500/5 border-amber-500/30 space-y-2">
                <p className="text-xs font-semibold text-amber-700">HR Adjustment</p>
                <FieldOverride
                  label={`HR Extra Deduction (${editing?.currency})`}
                  value={extraDeduction}
                  setValue={setExtraDeduction}
                  placeholder="0"
                  testid="pp-edit-deduction"
                  small
                />
                <div>
                  <label className="text-xs font-medium">HR Note</label>
                  <Textarea
                    value={hrNote}
                    onChange={(e) => setHrNote(e.target.value)}
                    placeholder="Reason for adjustments / audit trail"
                    className="mt-1 min-h-[60px] text-xs"
                    data-testid="pp-edit-note"
                  />
                </div>
              </div>
              <div className="border rounded-md p-3 bg-emerald-500/5 border-emerald-500/30 space-y-2">
                <p className="text-xs font-semibold text-emerald-700">CEO Bonus</p>
                <FieldOverride
                  label={`CEO Bonus Amount (${editing?.currency})`}
                  value={ceoBonus}
                  setValue={setCeoBonus}
                  placeholder="0"
                  testid="pp-edit-ceo-bonus"
                  small
                />
                <div>
                  <label className="text-xs font-medium">CEO Note</label>
                  <Textarea
                    value={ceoBonusNote}
                    onChange={(e) => setCeoBonusNote(e.target.value)}
                    placeholder="Reason for CEO bonus"
                    className="mt-1 min-h-[60px] text-xs"
                    data-testid="pp-edit-ceo-note"
                  />
                </div>
              </div>
            </div>

            {/* Live Payout Preview */}
            {whatIf && (
              <div className="border-2 border-emerald-500/40 bg-emerald-500/5 rounded-md p-3 space-y-1.5 sticky bottom-0" data-testid="pp-edit-whatif">
                <div className="flex items-center justify-between text-[11px] font-semibold text-emerald-700 mb-1">
                  <span>Live Payout Preview {whatIf.hasOverride && <span className="text-violet-600">· Bracket Override Active</span>}</span>
                  <span className="text-muted-foreground">Working Days: {editing?.working_days}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                  <span className="text-muted-foreground">Effective Gross:</span>
                  <span className="text-right font-mono">{fmtCur(whatIf.effGross, whatIf.currency)}</span>
                  <span className="text-muted-foreground">Commission:</span>
                  <span className="text-right font-mono text-emerald-600">+{fmtCur(whatIf.commission, whatIf.currency)}</span>
                  <span className="text-muted-foreground">Target Bonus:</span>
                  <span className="text-right font-mono text-emerald-600">+{fmtCur(whatIf.targetB, whatIf.currency)}</span>
                  <span className="text-muted-foreground">Manual Bonus:</span>
                  <span className="text-right font-mono text-emerald-600">+{fmtCur(whatIf.manualB, whatIf.currency)}</span>
                  <span className="text-muted-foreground">CEO Bonus:</span>
                  <span className="text-right font-mono text-emerald-600">+{fmtCur(whatIf.ceoB, whatIf.currency)}</span>
                  <span className="text-muted-foreground">LOP Deduction ({whatIf.lopDays}d):</span>
                  <span className="text-right font-mono text-red-600">−{fmtCur(whatIf.lopDed, whatIf.currency)}</span>
                  <span className="text-muted-foreground">Half-day Deduction ({whatIf.halfDays}):</span>
                  <span className="text-right font-mono text-red-600">−{fmtCur(whatIf.halfDed, whatIf.currency)}</span>
                  <span className="text-muted-foreground">HR Extra Deduction:</span>
                  <span className="text-right font-mono text-red-600">−{fmtCur(whatIf.extra, whatIf.currency)}</span>
                </div>
                <div className="border-t border-emerald-500/30 pt-1.5 flex justify-between font-bold text-base">
                  <span className="text-emerald-700">Final Payout</span>
                  <span className="font-mono text-emerald-700" data-testid="pp-edit-whatif-payout">{fmtCur(whatIf.finalPayout, whatIf.currency)}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="pp-edit-save">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Save &amp; Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
